import { Injectable } from '@angular/core'
import { api } from '@app/srv/api.service'
import { getState } from '@app/srv/store.service'
import {
  ApplyDiscountInput,
  BackendResponseFMResp,
  CartDiscount,
  CartInput,
  CartItem,
  CartItemInput,
  CartState,
  ExperimentalPrice,
  HardwareId,
  hwIdByProductKey,
  isRollingTrialKey,
  isThermometerKey,
  ProductKey,
  ProductType,
} from '@naturalcycles/shared'
import { getDefaultThermometer } from '../util/product.util'

@Injectable({ providedIn: 'root' })
export class CartService {
  public async replace(
    productKeys: ProductKey[],
    discount?: CartDiscount,
    hwId?: HardwareId,
  ): Promise<CartState> {
    const newItems: CartItemInput[] = productKeys.map(key => ({
      key,
      quantity: 1,
    }))

    return await api
      .post<BackendResponseFMResp>('carts', {
        json: {
          items: newItems,
          discount,
          hwId,
        } satisfies CartInput,
      })
      .then(r => r.backendResponse.cart!)
  }

  public async update({
    items,
    discount,
    hwId,
    validateShippingCountry,
    paidWithIAP,
  }: CartUpdateInput): Promise<CartState> {
    const queryParams = new URLSearchParams({
      validateShippingCountry: String(!!validateShippingCountry),
      paidWithIAP: String(!!paidWithIAP),
    }).toString()

    let updatedItems = items
    let updatedHwId = hwId
    const isMorethanOneThermometer = items.filter(item => isThermometerKey(item.key)).length > 1
    const defaultThermometer = getDefaultThermometer()
    const isDefaultThermometerIncart = items.some(item => item.key === defaultThermometer)

    /**
     * We need to ensure that if there are multiple thermometers in the cart,
     * we only keep the default one (if it exists) and remove the rest.
     * This is to prevent issues with two thermometers in the cart when we switch between available thermometers,
     * for example, when we switch from T2 to T3 in the US.
     * We also need to ensure that the hwId is set to the default thermometer's hwId.
     */
    if (isMorethanOneThermometer && isDefaultThermometerIncart) {
      updatedItems = updatedItems.filter(item => {
        if (!isThermometerKey(item.key)) return true
        return item.key === defaultThermometer
      })
      updatedHwId = hwIdByProductKey[defaultThermometer]
    }

    return await api
      .post<BackendResponseFMResp>(`carts?${queryParams}`, {
        json: {
          items: updatedItems,
          discount,
          hwId: updatedHwId,
        } satisfies CartInput,
      })
      .then(r => r.backendResponse.cart!)
  }

  public async getItem(key: ProductKey): Promise<CartState> {
    return await api.get<CartState>(`carts/item/${key}`)
  }

  public async updateCartItemQuantity(key: ProductKey, quantity: number): Promise<void> {
    const items = this.getCartItemInputs()
    const selectedItem = items.find(i => i.key === key)
    if (selectedItem) {
      items.forEach(item => {
        if (item.key === selectedItem.key) item.quantity = quantity
      })
    } else {
      items.push({ key, quantity })
    }
    const offer = this.getProductOffer(items)
    await this.update({ items, discount: offer })
  }

  public async removeItemFromCart(key: ProductKey): Promise<void> {
    const items = this.getCartItemInputs()
    const updated = items.filter(i => i.key !== key)
    const offer = this.getProductOffer(updated)
    await this.update({ items: updated, discount: offer })
  }

  public async removeDiscountFromCart(): Promise<void> {
    const { cart } = getState()
    await this.update({ items: cart.items, hwId: cart.hwId })
  }

  private getCartItems(): CartItem[] {
    return getState().cart.items
  }

  public getCartItemInputs(): CartItemInput[] {
    return this.getCartItems().map(item => ({
      key: item.key,
      quantity: item.quantity,
      variant: item.variant,
    }))
  }

  public getCartDiscount(): CartDiscount | undefined {
    return getState().cart.discount
  }

  private getProductOffer(cartItems: CartItemInput[]): CartDiscount | undefined {
    const { product } = getState()
    const productOffer = product.items.find(productItem => {
      return cartItems.find(item => item.key === productItem.key && productItem.offer)
    })
    if (productOffer?.offer?.code) {
      return { code: productOffer.offer.code }
    }
  }

  public getSubscriptionType(): ProductKey | undefined {
    const { cart } = getState()
    const subscription = cart.items.find((item: CartItem) => item.type === ProductType.SUBSCRIPTION)

    return subscription?.key
  }

  /**
   * @description
   * We use this method at CHECKOUT to validate a discount code before applying it to the cart.
   *
   * @note
   * - If the discount code's conditions aren't met, an exception will be thrown with the code DISCOUNT_CODE_VALIDATION_FAILURE.
   * - If zero discounts can be applied, the {@link CartState.discountStatus} will be {@link CartDiscountStatus.Mismatch}.
   * - If at least one discount can be applied, the {@link CartState.discountStatus} will be {@link CartDiscountStatus.Applied}.
   *
   * In the case that there's a 'Mismatch' between the user's product selection and the discount code's product offering
   * We ask the user to replace their cart with the products that the discount code is intended for.
   *
   */
  async validateDiscount(input: ApplyDiscountInput): Promise<CartState> {
    return await api.put(`carts/discount/validate`, {
      json: input,
    })
  }

  isRecurringSubscription(cart: CartState): boolean {
    return cart.items.some(i => i.type === ProductType.SUBSCRIPTION && i.recurring)
  }

  hasValidThermometerSelection(): boolean {
    const therm = getState().cart.items.find(item => item.key === ProductKey.THERMOMETER)
    return !therm || !!therm.variant
  }

  getNetTotal(cart: CartState): ExperimentalPrice {
    return ExperimentalPrice.fromCents(cart.netTotalAmount, cart.currency)
  }

  requiresPayment(cart: CartState): boolean {
    return (
      cart.netTotalAmount > 0 ||
      this.isRecurringSubscription(cart) ||
      cart.items.some(i => isRollingTrialKey(i.key))
    )
  }
}

interface CartUpdateInput {
  items: CartItemInput[]
  discount?: CartDiscount
  hwId?: HardwareId
  validateShippingCountry?: boolean
  paidWithIAP?: boolean
}
